import { iconSizes } from '@mds/tokens';
import {
  alertClasses,
  alertTitleClasses,
  buttonClasses,
  iconButtonClasses,
  type AlertProps,
  type Components,
} from '@mui/material';
import { themeVars } from '../themeVars';
import { paletteColorNames, type Theme } from './utils';

export const closeIconClass = 'MdsAlertCloseIcon';

const cssLength = (value: number | string) => (typeof value === 'number' ? `${value}px` : value);

type AlertVariantProps = Partial<AlertProps> & {
  colorSeverity?: string;
  ownerState: Partial<AlertProps>;
};

const alertVariantMatches =
  (...variants: string[]) =>
  (props: AlertVariantProps) =>
    variants.includes(props.variant ?? props.ownerState.variant ?? '');

const alertIconColorStyle = {
  [`& .${alertClasses.icon}`]: {
    color: 'var(--mds-Alert-iconColor)',
  },
};

export const MuiAlert: Components<Theme>['MuiAlert'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      position: 'relative',
      padding: theme.spacing(1.5),
      border: 'var(--mds-Alert-border)',
      borderRadius: themeVars(theme).shape.borderRadius,
      backgroundColor: 'var(--mds-Alert-bg)',
      color: 'var(--mds-Alert-textColor)',
      '--mds-Alert-borderWidth': cssLength(theme.shape.borderWidth),
      fontSize: theme.typography.body2.fontSize,

      // Normally we don't override CSS of nested components - instead we would use a
      // React prop, like <Button noPadding>. However we don't intend to use any buttons
      // inside alerts other than action buttons (default color text buttons).
      [`.${buttonClasses.root}`]: {
        height: 32,
        minHeight: 32,
        paddingBlock: 0,
      },

      [`.${buttonClasses.text}`]: {
        '--mds-Alert-action-padding-inline': '8px',
        paddingInline: `var(--mds-Alert-action-padding-inline)`,
        paddingBlock: 0,
        minWidth: 0,
        border: 0,
        color: 'var(--mds-Alert-textColor)',
        '&:focus-visible': {
          boxShadow: `0 0 0 2px ${themeVars(theme).palette.border.focused}`,
        },
      },

      // This aligns action buttons horizontally when they are placed below text content.
      // See comment above - alert action buttons have limited placement options by
      // design.
      [`.${buttonClasses.text}:first-of-type`]: {
        marginLeft: `calc(-1 * var(--mds-Alert-action-padding-inline))`,
        minWidth: 0,
      },
    }),

    icon: ({ theme }) => ({
      alignSelf: 'center',
      color: 'var(--mds-Alert-iconColor)',
      fontSize: iconSizes.lg,
      height: iconSizes.lg,
      marginRight: theme.spacing(1.5),
      minWidth: iconSizes.lg,
      padding: 0,
      width: iconSizes.lg,
    }),

    message: ({ theme }) => ({
      alignSelf: 'center',
      color: 'var(--mds-Alert-textColor)',
      fontSize: theme.typography.body2.fontSize,
      overflow: 'initial',
      padding: 0,

      [`.${alertTitleClasses.root}`]: {
        color: 'inherit',
        fontSize: 'inherit',
      },
    }),

    action: ({ theme }) => ({
      alignItems: 'center',
      marginRight: 0,
      paddingLeft: theme.spacing(2),
      paddingRight: 0,
      paddingTop: 0,

      [`.${buttonClasses.text}`]: {
        marginLeft: theme.spacing(1),
      },

      [`& .${iconButtonClasses.root}`]: {
        color: 'var(--mds-Alert-textColor)',
        marginRight: -4,
      },

      [`& .${closeIconClass}`]: {
        fontSize: 'inherit',
      },
    }),
  },
  variants: [
    ...paletteColorNames.map((color) => ({
      props: (props: AlertVariantProps) =>
        props.colorSeverity === color || props.ownerState.color === color || props.ownerState.severity === color,
      style: ({ theme }: { theme: Theme }) => {
        const paletteColor = themeVars(theme).palette[color];
        return {
          '--mds-Alert-softBg': paletteColor.softBg,
          '--mds-Alert-softText': paletteColor.softText,
          '--mds-Alert-iconColor': paletteColor.border,
          '--mds-Alert-solidBg': paletteColor.solidBg,
          '--mds-Alert-solidColor': paletteColor.solidColor,
          [`& .${alertClasses.icon} .${iconButtonClasses.root}`]: {
            borderColor: 'var(--mds-Alert-iconColor)',
          },
          [`& .${alertClasses.icon} .${iconButtonClasses.root}, & .${alertClasses.action} .${iconButtonClasses.root}`]:
            {
              fontSize: iconSizes.md,
              height: 32,
              width: 32,
            },
        };
      },
    })),
    {
      props: alertVariantMatches('soft', 'standard'),
      style: {
        '--mds-Alert-bg': 'var(--mds-Alert-softBg)',
        '--mds-Alert-border': '0',
        '--mds-Alert-textColor': 'var(--mds-Alert-softText)',
        ...alertIconColorStyle,
      },
    },
    {
      props: alertVariantMatches('outline', 'outlined'),
      style: {
        '--mds-Alert-bg': 'var(--mds-Alert-softBg)',
        '--mds-Alert-border': 'var(--mds-Alert-borderWidth) solid var(--mds-Alert-iconColor)',
        '--mds-Alert-textColor': 'var(--mds-Alert-softText)',
        borderLeftWidth: 'calc(var(--mds-Alert-borderWidth) * 2)',
        ...alertIconColorStyle,
      },
    },
    {
      props: { variant: 'filled' },
      style: ({ theme }: { theme: Theme }) => ({
        '--mds-Alert-bg': 'var(--mds-Alert-solidBg)',
        '--mds-Alert-border': '0',
        '--mds-Alert-iconColor': 'var(--mds-Alert-solidColor)',
        '--mds-Alert-textColor': 'var(--mds-Alert-solidColor)',
        fontWeight: theme.typography.fontWeightMedium,
        [`& .${alertClasses.icon} .${iconButtonClasses.root}`]: {
          borderColor: 'var(--mds-Alert-solidColor)',
          fontSize: iconSizes.md,
        },
      }),
    },
  ],
};

export const MuiAlertTitle: Components<Theme>['MuiAlertTitle'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      fontSize: theme.typography.body2.fontSize,
      marginTop: 0,
    }),
  },
};
